import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import isNil from 'lodash/isNil';
import styled from 'styled-components';
import { setActiveReserveListId } from '../../store/reserve/actions';
import ProductList from '../../shared/common/ProductList';
import FilterBar from '../../shared/common/FilterBar';
import { RESERVE_DETAILS_SORT_BY } from '../../shared/common/SortByDropDown';
import LoadingOverlay from '../../shared/components/LoadingOverlay';
import { LoadingWrapper } from '../../shared/components/LoadingWrapper';
import Pagination, { getPaginationStateObject } from '../../shared/common/Pagination';
import ModalDialog from '../../shared/components/modal/ModalDialog';
import historyUtils, { modifyURLState, modifyURLStates } from '../../shared/utils/utils-routing';
import { PATH_CHECKOUT, PATH_ORDERS, PATH_RESERVE } from '../../store/constants';
import { displayReserveDetail } from '../../store/header/actions';
import { showNotification, TYPE_WARNING } from '../../store/notifications/actions';
import NoElementsFound, { NO_ELEMENTS_RESERVES } from '../../shared/components/NoElementsFound';
import {
    LOANFORMAT_EAUDIOBOOK_OBJECT,
    LOANFORMAT_EAUDIOBOOKS,
    LOANFORMAT_EBOOK_OBJECT,
    LOANFORMAT_EBOOKS
} from '../../store/loanFormat/actions';
import { ContainerInnerWithColumns } from '../../shared/common/ContainerInner';
import { FilterAndProductListWrapper } from '../../shared/common/FilterAndProductListWrapper';
import { ReserveDetailSettings } from './components/ReserveDetailSettings';
import ReserveDetailHeader from './components/ReserveDetailHeader';
import { RESERVE_STATUS_CLOSED } from './constants';
import ReserveListItem from './components/ReserveListItem';
import { AppState } from '../../store/appState';
import { useHistory, useLocation, useParams } from 'react-router';
import { LoanFormatState } from '../../store/loanFormat/state';
import { useQueryParams } from '../../shared/hooks/useQueryParams';
import { useReserveReportSummary } from './api/reserve-list/useReserveReportSummary';
import { useReserveReportProducts } from './api/reserve-list/useReserveReportProducts';
import { useCopyAmountChange } from './api/reserve-list-item/useCopyAmountChange';
import { useFilterAcceptReject } from './api/reserve-list-item/useFilterAcceptReject';
import { useReopenReserveList } from './api/reserve-list/useReopenReserveList';
import { useGenerateReserveReportMutation } from './api/reserve-report/useGenerateReserveReportMutation';
import { useSendForApproval } from '../../api/reserveLists/useSendForApproval';
import queryString from 'query-string';
import { ProductSorting } from '../../../models/Product';
import { deserializeFilter } from '../../shared/utils/deserializeFilter';
import { ResponsiveFilterBar } from '../../shared/filter/ResponsiveFilterBar';
import { fetchReserveListDetailAggregationsQuickSearch } from '../../api/reserveLists/fetchReserveListDetailAggregationsQuickSearch';
import { AuthenticationState } from '../../store/authentication/state';
import { FetchAggregations } from '../../api/listContent/fetchAggregations';
import { fetchReserveListDetailAggregations } from '../../api/reserveLists/fetchReserveListDetailAggregations';
import { QueryKeyBuilder } from '../../shared/filter/useAggregationsQuery';
import { FetchAggregationsQuickSearch } from '../../api/listContent/fetchAggregationsQuickSearch';
import { defaultVisibleFilterGroups } from '../../shared/filter/defaultVisibleFilterGroups';
import { useTranslation } from 'react-i18next';

const SORT_BY_QUERY_PARAM = 'sortBy';
const PAGE_QUERY_PARAM = 'page';

export const DEFAULT_PAGE_SIZE = 45;

const IncludeAllIcon = styled.i`
    && {
        && {
            color: #7fc31c;
        }
    }
`;

const ExcludeAllIcon = styled.i`
    && {
        && {
            color: #ff0c00;
        }
    }
`;

const IncludeAllButton = styled.button`
    :hover {
        i {
            color: inherit !important;
        }
    }
`;

export const ReserveDetail = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { search } = useLocation();
    const spoc = useSelector((state: AppState) => state.authentication.spoc);
    const authentication = useSelector((state: AppState) => state.authentication);
    const dispatch = useDispatch();

    const { reserveListId } = useParams<{ reserveListId: string }>();

    useLayoutEffect(() => {
        dispatch(displayReserveDetail());
    }, [dispatch]);

    const [showReopenDialog, setShowReopenDialog] = useState(false);
    const [showOutdatedWarning, setShowOutdatedWarning] = useState(false);
    const [outdatedWarningWasShown, setOutdatedWarningWasShown] = useState(false);

    const { data: reserveReportSummary, isLoading: isSummaryLoading } = useReserveReportSummary(reserveListId);
    const { mutate: generateReserveReport } = useGenerateReserveReportMutation();

    const isClosedState = reserveReportSummary?.status === RESERVE_STATUS_CLOSED;
    const isOrderedState = !isNil(reserveReportSummary?.orderNumber);

    const {
        mutateAsync: sendForApproval,
        isLoading: isLoadingSendForApproval,
        errorMessage: sendForApprovalErrorMessage
    } = useSendForApproval({ listId: reserveListId });

    useEffect(() => {
        dispatch(setActiveReserveListId(reserveListId));
        return () => {
            dispatch(setActiveReserveListId(''));
        };
    }, [dispatch, reserveListId]);

    /**
     * The `displayFilter` query param is either there or not. There is no value attached to it.
     * Examples:
     *      with:       `/collection/by-genre/all?displayFilter
     *      without:    `/collection/by-genre/all
     *
     * Parsing this query param with the `query-string` library will result in either null value or undefined.
     * This is the way how we can identify if the filter should be open or not.
     * Unfortunately, we can not replicate this behaviour with the `useQueryParams` hook.
     *
     * Of course it would be nicer to have a boolean value.
     * But that is a breaking change in regard to the URL structure.
     */
    const { displayFilter: displayFilterQueryParam } = queryString.parse(search);
    const displayFilter = displayFilterQueryParam === null;

    const {
        page: { value: pageParam = 1 },
        pageSize: { value: pageSizeParam },
        sortBy: { value: sortByParam },
        filter: { value: filterQueryParam }
    } = useQueryParams({
        [PAGE_QUERY_PARAM]: 'integer',
        [SORT_BY_QUERY_PARAM]: 'string',
        pageSize: 'integer',
        filter: 'string'
    });
    const page = pageParam - 1 ?? 0;
    const sortBy = sortByParam ?? ProductSorting.RELEASE_DATE_NEW_TO_OLD;
    const pageSize = pageSizeParam ?? DEFAULT_PAGE_SIZE;
    const filter = deserializeFilter(filterQueryParam);

    const { data: productData, isLoading: isProductsLoading } = useReserveReportProducts({
        reserveListId,
        filterMap: filter,
        pagination: {
            page,
            pageSize,
            sortBy
        }
    });

    const { mutate: changeCopyAmount } = useCopyAmountChange(reserveListId);
    const { mutate: doReopen } = useReopenReserveList(reserveListId);
    const { mutate: filterAcceptReject, isLoading: isFilterAcceptLoading } = useFilterAcceptReject(reserveListId, {
        page,
        pageSize,
        sortBy
    });

    const products = productData?.hits ?? [];
    const totalProducts = productData?.totalHits ?? 0;

    //region FILTERBAR_V2
    const visibleFilterGroups = useMemo(() => reserveDetailPageFilterGroups(authentication), [authentication]);
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => {
            return ['reserveDetailPage', reserveListId, 'aggregations', filters, expandedFilterGroups];
        },
        [reserveListId]
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options => fetchReserveListDetailAggregations({ ...options, reserveListId }),
        [reserveListId]
    );
    const fetchAggregationsQuickSearchCallback = useCallback<FetchAggregationsQuickSearch>(
        options => fetchReserveListDetailAggregationsQuickSearch({ ...options, reserveListId }),
        [reserveListId]
    );
    //endregion

    useEffect(() => {
        if (reserveReportSummary?.outdatedDueToOrder && !outdatedWarningWasShown) {
            setShowOutdatedWarning(true);
            setOutdatedWarningWasShown(true);
        }
    }, [outdatedWarningWasShown, reserveReportSummary?.outdatedDueToOrder]);

    const lockedSwitchNotification = useCallback(
        (acceptReject: 'Excluding' | 'Including') => {
            dispatch(showNotification(`${acceptReject} this product is still processing`, TYPE_WARNING, 1500));
        },
        [dispatch]
    );

    const closeRegenerateModal = useCallback(() => {
        setShowOutdatedWarning(false);
    }, []);

    const acceptRegeneration = useCallback(() => {
        setShowOutdatedWarning(false);

        const generateSettings = {
            generatedWithDefaults: reserveReportSummary?.generatedWithDefaults,
            loanFormatDefaults: reserveReportSummary?.loanFormatSettings
        };

        generateReserveReport({ generateSettings });
    }, [generateReserveReport, reserveReportSummary?.generatedWithDefaults, reserveReportSummary?.loanFormatSettings]);

    const onCopyAmountChange = useCallback(
        (productId: string, amount: number, command: string) => {
            changeCopyAmount({ productId, numCopies: amount, command });
        },
        [changeCopyAmount]
    );

    const reopenCancel = useCallback(() => {
        setShowReopenDialog(false);
    }, []);

    const reopen = useCallback(() => {
        doReopen();
        setShowReopenDialog(false);
    }, [doReopen]);

    const eaudiobooksAmount = reserveReportSummary?.summary?.eaudiobooksAmount ?? 0;
    const ebooksAmount = reserveReportSummary?.summary?.ebooksAmount ?? 0;

    const proceedToCheckout = useCallback(() => {
        if (eaudiobooksAmount > 0 || ebooksAmount > 0) {
            historyUtils.pushUrl(`${PATH_RESERVE}/${reserveListId}${PATH_CHECKOUT}`);
        } else {
            dispatch(showNotification(t('notifications.reserveDetail.proceedToCheckOut.warning'), TYPE_WARNING));
        }
    }, [dispatch, reserveListId, eaudiobooksAmount, ebooksAmount, t]);

    const proceedToOrder = useCallback(() => {
        if (!reserveReportSummary?.orderNumber) {
            dispatch(showNotification(t('notifications.reserveDetail.proceedToOrder.warning'), TYPE_WARNING));
        } else {
            historyUtils.pushUrl(`${PATH_ORDERS}/${reserveReportSummary?.orderNumber}`);
        }
    }, [dispatch, reserveReportSummary?.orderNumber, t]);

    const proceedToReopen = useCallback(() => {
        if (reserveReportSummary?.outdatedDueToGeneration) {
            setShowReopenDialog(true);
        } else {
            reopen();
        }
    }, [reopen, reserveReportSummary?.outdatedDueToGeneration]);

    const setSortBy = (nextSortBy: string) => {
        const pageQueryState = getPaginationStateObject();
        const sortByQueryState = { name: SORT_BY_QUERY_PARAM, value: nextSortBy, encode: false };

        const nextSearch = modifyURLStates(history.location.search, [pageQueryState, sortByQueryState]);

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    const setPage = (nextPage: number) => {
        const nextSearch = modifyURLState(
            history.location.search,
            PAGE_QUERY_PARAM,
            nextPage !== 0 ? nextPage + 1 : undefined,
            false
        );

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    function onFilterDisplayToggle() {
        const nextSearch = modifyURLState(history.location.search, 'displayFilter', !displayFilter, false);
        history.replace({ pathname: history.location.pathname, search: nextSearch });
    }

    const reportHasEbooks = reserveReportSummary?.loanFormatSettings?.[LOANFORMAT_EBOOKS];
    const reportHasEaudiobooks = reserveReportSummary?.loanFormatSettings?.[LOANFORMAT_EAUDIOBOOKS];

    const ebookReserveRatio = reserveReportSummary?.loanFormatSettings?.[LOANFORMAT_EBOOKS]?.reserveRatio ?? 0;
    const eaudiobookReserveRatio =
        reserveReportSummary?.loanFormatSettings?.[LOANFORMAT_EAUDIOBOOKS]?.reserveRatio ?? 0;

    let reserveLoanFormat: LoanFormatState | null = null;
    if (reportHasEbooks && !reportHasEaudiobooks) {
        reserveLoanFormat = LOANFORMAT_EBOOK_OBJECT;
    } else if (!reportHasEbooks && reportHasEaudiobooks) {
        reserveLoanFormat = LOANFORMAT_EAUDIOBOOK_OBJECT;
    }

    const isAcceptRejectAllDisabled = isClosedState || isOrderedState || totalProducts <= 0 || isProductsLoading;

    return (
        <ContainerInnerWithColumns className={classNames({ 'min-h-screen': isProductsLoading }, 'reserve-detail-list')}>
            <LoadingWrapper>
                <LoadingOverlay loading={isProductsLoading} />
            </LoadingWrapper>
            <ModalDialog show={showReopenDialog} onCloseButtonClick={reopenCancel} title="Re-open">
                <p>{t('reserveReports.reserveReport.viewReport.reopenDialog')}</p>
                <div className="dlb-actions">
                    <a className="lk" onClick={reopenCancel}>
                        {t('cancel')}
                    </a>
                    <a className="lk lk-primary" onClick={reopen}>
                        {t('reserveReports.reserveReport.viewReport.reopen')}
                    </a>
                </div>
            </ModalDialog>
            <ModalDialog show={showOutdatedWarning} onCloseButtonClick={closeRegenerateModal} title="Report Outdated">
                <p>{t('reserveReports.reserveReport.viewReport.outDatedDialog')}</p>
                <div className="dlb-actions">
                    <a className="lk" onClick={closeRegenerateModal}>
                        {t('reserveReports.reserveReport.viewReport.dontRegenerate')}
                    </a>
                    <a className="lk lk-primary" onClick={acceptRegeneration}>
                        {t('reserveReports.reserveReport.viewReport.regenerate')}
                    </a>
                </div>
            </ModalDialog>

            <ReserveDetailHeader
                isOrdered={isOrderedState}
                loading={isSummaryLoading}
                proceedToCheckout={proceedToCheckout}
                proceedToOrder={proceedToOrder}
                proceedToReopen={proceedToReopen}
                reserveReportDetails={reserveReportSummary}
                spoc={spoc}
                authentication={authentication}
                loanFormat={reserveLoanFormat}
                sendForApproval={sendForApproval}
                isLoadingSendForApproval={isLoadingSendForApproval}
                sendForApprovalError={sendForApprovalErrorMessage}
            />

            <ReserveDetailSettings
                reserveReportDetails={reserveReportSummary}
                ebookReserveRatio={ebookReserveRatio}
                eAudiobookReserveRatio={eaudiobookReserveRatio}
            />

            {products.length > 0 ? (
                <div className="reserve-list-control-bar">
                    <div className="reserve-list-filterbar-wrapper">
                        <FilterBar
                            dispatch={dispatch}
                            sorting={RESERVE_DETAILS_SORT_BY}
                            onFilterDisplayToggle={onFilterDisplayToggle}
                            hideGroupBy={true}
                            hideDisplayType={true}
                            hideSelectButtons={true}
                            totalProducts={totalProducts}
                            onPageChange={setPage}
                            onSortChange={setSortBy}
                            isFilterToggleReplaced={isOrderedState}
                            leftColumnText={'Items'}
                            sortBy={sortBy}
                            displayFilter={displayFilter}
                        />
                    </div>
                    <div className="reserve-action-buttons" data-displayname="ReserveActionButtons">
                        {/* We have to overwrite display here because it is defined in material link and bt classes */}
                        <IncludeAllButton
                            disabled={isFilterAcceptLoading}
                            className={classNames('bt', 'bt-bordered', 'material-link', {
                                disabled: isAcceptRejectAllDisabled || isFilterAcceptLoading
                            })}
                            onClick={() => filterAcceptReject('accept')}
                            style={{ display: 'inline-flex' }}
                        >
                            <IncludeAllIcon className="material-icons">done</IncludeAllIcon>
                            {filterQueryParam ? (
                                <span>{t('reserveReports.reserveReport.viewReport.includeAllFiltered')}</span>
                            ) : (
                                <span>{t('reserveReports.reserveReport.viewReport.includeAll')}</span>
                            )}
                        </IncludeAllButton>
                        <IncludeAllButton
                            disabled={isFilterAcceptLoading}
                            className={classNames('bt', 'bt-bordered', 'negative', 'material-link', {
                                disabled: isAcceptRejectAllDisabled || isFilterAcceptLoading
                            })}
                            onClick={() => filterAcceptReject('reject')}
                            style={{ display: 'inline-flex' }}
                        >
                            <ExcludeAllIcon className="material-icons">clear</ExcludeAllIcon>
                            {filterQueryParam ? (
                                <span>{t('reserveReports.reserveReport.viewReport.excludeAllFiltered')}</span>
                            ) : (
                                <span>{t('reserveReports.reserveReport.viewReport.excludeAll')}</span>
                            )}
                        </IncludeAllButton>
                    </div>
                </div>
            ) : null}

            <FilterAndProductListWrapper>
                {displayFilter ? (
                    <ResponsiveFilterBar
                        visibleFilterGroups={visibleFilterGroups}
                        buildQueryKey={aggregationsQueryKeyBuilder}
                        fetchAggregations={fetchAggregations}
                        fetchAggregationsQuickSearch={fetchAggregationsQuickSearchCallback}
                    />
                ) : null}
                {products.length > 0 ? (
                    <div className="ca-main">
                        <ProductList
                            products={products}
                            totalProducts={totalProducts}
                            displayType="list"
                            listItemComponent={ReserveListItem}
                            onCopyAmountChange={onCopyAmountChange}
                            reserveListId={reserveListId}
                            hideAcceptReject={isClosedState || isOrderedState || isFilterAcceptLoading}
                            lockedSwitchNotification={lockedSwitchNotification}
                            currentPage={page}
                        />
                        <div style={{ marginBottom: '100px' }}>
                            <Pagination
                                totalItems={totalProducts}
                                pageSize={pageSize}
                                currentPage={page}
                                onPageChange={setPage}
                            />
                        </div>
                    </div>
                ) : (
                    <NoElementsFound type={NO_ELEMENTS_RESERVES} />
                )}
            </FilterAndProductListWrapper>
        </ContainerInnerWithColumns>
    );
};

const reserveDetailPageFilterGroups = (authentication: AuthenticationState) => [
    ...defaultVisibleFilterGroups(authentication).filter(
        item => !['releaseFrequency', 'releaseQuantity', 'mucPrice'].includes(item)
    ),
    // reserve report uses the legacy (non-MUC) price filter "price" instead of "mucPrice"
    'price'
];
